import type { Database } from "@shared/types/database";
import type { SignalRelationshipResult } from "@/components/RelationshipResults";
import type {
  CanonicalPractice,
  PracticeClassification,
  PracticeKey,
  PracticeLensSummary,
} from "./practice-lens-ui";

export type {
  CanonicalPractice,
  PracticeClassification,
  PracticeKey,
  PracticeLensSummary,
};

/**
 * Phase D signals — domain types.
 *
 * These are the UI-facing projection the feed + modal render. Enum unions are
 * pulled from the generated database types so the live adapter maps
 * v_signal_feed rows into this shape with full type checking. Fields that are
 * mock in Phase D are marked: scfScore/tier (E1, added to the v2 view later by
 * Codex), verification (E5), and the SignalContent key names (E2) which may
 * shift at the live swap and are therefore isolated behind the adapter.
 */

export type OwnershipState =
  Database["public"]["Enums"]["signal_ownership_state"];
export type LifecycleStatus = Database["public"]["Enums"]["signal_status"];
export type ArchiveReason =
  Database["public"]["Enums"]["signal_archive_reason_v2"];

export type Tier = 1 | 2 | 3;

/** E5 — verification is mock in Phase D. */
export type VerificationStatus = "verified" | "quarantined" | "unverified";

/**
 * Signal type taxonomy — the canonical categories the feed filters on, mirroring
 * the get_signal_feed `signal_type` argument. Each maps to a leading card icon
 * (see score.ts SIGNAL_TYPE_META).
 */
export type SignalType =
  | "rfp"
  | "hiring"
  | "m_and_a"
  | "transformation"
  | "leadership"
  | "market_intel"
  | "target_account";

/** Filter selection: a single signal type, or "all" (the default, no filter). */
export type SignalTypeFilter = SignalType | "all";

/**
 * Score composition (Feed restyle · score hover tooltip). Mock in Phase D; binds
 * to the live `score_breakdown` projection once Codex ships it. Components sum
 * toward scfScore; targetAccountBonus is 0 unless the signal is a target account.
 */
export interface ScoreBreakdown {
  signalWeight: number;
  capabilityFit: number;
  recency: number;
  targetAccountBonus: number;
  /** One-line rationale shown under the breakdown rows. */
  summary: string;
}

/**
 * E2 — signal_content shape. Key names may adjust at the live swap; nothing
 * outside the adapter layer should read raw signal_content, only this type.
 */
export interface SignalContent {
  summary: string;
  why_it_matters: string;
  recommended_action: string;
  talk_track: string;
}

/** The projection every feed/card/modal component renders. */
/**
 * Honest activation context projected by get_signal_detail (v13). Present only on
 * detail rows; feed rows and older cached payloads omit it, and a null
 * `relationship` means scope did not resolve — both render the panel empty state,
 * never a default implying warmth.
 */
export interface ActivationContext {
  relationship: "existing_client" | "prior_relationship" | "net_new" | null;
  engagementDetail: {
    activeCount: number;
    practiceCount: number;
    dormantCount: number;
  };
  lastTouch: string | null;
  valueTier: "Tier 1" | "Tier 2" | "Tier 3" | null;
}

export interface FeedSignal {
  id: string;
  /** E1 — mock in Phase D; sourced from the v2 view once Codex ships it. */
  scfScore: number;
  /** Derived from scfScore (Tier 1 = 80+, Tier 2 = 65–79, Tier 3 = <65). */
  tier: Tier;
  company: string;
  headline: string;
  content: SignalContent;
  /** Feed restyle — signal-type category (drives the leading card icon + filter). */
  signalType: SignalType;
  /** Feed restyle — personal saved/star state (toggle_signal_save). */
  saved: boolean;
  /** Feed restyle — score composition for the hover tooltip (mock in Phase D). */
  scoreBreakdown: ScoreBreakdown;
  source: string;
  practice: string | null;
  eventType: string | null;
  confidence: number | null;
  geography: string | null;
  ownershipState: OwnershipState;
  lifecycleStatus: LifecycleStatus;
  /**
   * Canonical stage from get_signal_detail's canonical_stage_outcome projection
   * (R-3): v_signal_stage_outcome_current.current_stage_key, derived from the
   * ordered ledger. Replaces the legacy pursuit_stage cache read. Detail-only —
   * feed rows do not carry the projection, so this is absent on feed signals.
   */
  canonicalStageKey?:
    | "assigned"
    | "stage_outreach_made"
    | "stage_meeting_scheduled"
    | "stage_proposal"
    | "won"
    | null;
  /** Persisted Seamless results hydrated by get_signal_detail; never triggers search. */
  foundContacts?: FoundSignalContact[];
  claimedBy: string | null;
  claimedByName: string | null;
  assignedTo: string | null;
  assignedToName: string | null;
  assignedByViewer: boolean;
  /** Server-computed on the live view; mock on fixtures. */
  isAssignmentStale: boolean;
  archiveReason: ArchiveReason | null;
  /** E5 — mock in Phase D. */
  verification: VerificationStatus;
  /** Open follow-up due date (ISO) if a follow_up_set event is outstanding. */
  followUpDueAt: string | null;
  firstObservedAt: string | null;
  lastObservedAt: string | null;
  deadlineAt: string | null;
  url: string | null;
  /** Honest activation context (get_signal_detail v13). Detail-only: absent on
   *  feed rows and older cached payloads → the panel empty state. */
  activationContext?: ActivationContext | null;
  /** Canonical organization id for the signal's account (get_signal_detail v14).
   *  Detail-only: absent on feed rows. Keys the Slice B read surfaces
   *  (get_slice_b_surfaces canonical_org_id). See useCanonicalOrgId in sliceB.ts. */
  canonicalOrgId?: string | null;
  /**
   * Effective practice classification (get_signal_detail practice_classification,
   * 2W). Detail-only: absent on feed rows and on older cached payloads — the
   * modal's read-only panel renders its skeleton while hydrating rather than
   * reading this field before the detail fetch lands. `null` means
   * classification unavailable; an empty `practices` array means not yet
   * routed to a practice.
   */
  practiceClassification?: PracticeClassification | null;
}

/** Ownership tabs (spec §4 predicates, §5 labels). */
export type FeedTab =
  | "all"
  | "unclaimed"
  | "claimed_by_me"
  | "saved"
  | "follow_up_due"
  | "archived";

export interface FeedTabDef {
  key: FeedTab;
  label: string;
  /** Empty-state copy (spec §5). */
  emptyTitle: string;
  emptyBody: string;
}

export const FEED_TABS = [
  {
    key: "all",
    label: "All",
    emptyTitle: "No signals in the feed yet.",
    emptyBody: "New signals appear here as they are observed.",
  },
  {
    key: "unclaimed",
    label: "Unclaimed",
    emptyTitle: "Every signal is owned. Nothing waiting.",
    emptyBody: "Unclaimed signals land here the moment they arrive.",
  },
  {
    key: "claimed_by_me",
    label: "Claimed by Me",
    emptyTitle: "You haven't claimed a signal yet.",
    emptyBody: "Claimed signals appear here.",
  },
  {
    key: "saved",
    label: "Saved",
    emptyTitle: "You haven't saved a signal yet.",
    emptyBody: "Star a signal to keep it here for quick access.",
  },
  {
    key: "follow_up_due",
    label: "Follow-up Due",
    emptyTitle: "No follow-ups scheduled.",
    emptyBody: "Set a follow-up on a signal to see it here.",
  },
  {
    key: "archived",
    label: "Archived",
    emptyTitle: "Nothing archived.",
    emptyBody: "Archived signals are kept here for reference.",
  },
] as const satisfies readonly FeedTabDef[];

/* ============================================================
   Adapter contracts (spec §8) — mock + live share these.
   ============================================================ */

/** Feed sort order (get_signal_feed v14 `sort` argument). Absent server-side ==
 *  highest_score; the client always sends an explicit value. Session-local only
 *  (no persistence) — a fresh visit starts on most_recent. */
export type SortValue = "most_recent" | "highest_score";

export interface FeedPageParams {
  tab: FeedTab;
  viewerId: string;
  /** Signal-type filter; "all" applies no type constraint. Composes with tab. */
  signalType: SignalTypeFilter;
  /** Keyset cursor from the previous page; null for the first page. */
  cursor: string | null;
  limit: number;
  /** Sort order. Part of the feed query key so switching restarts the cursor
   *  walk (cursors are never reused across sorts; the server rejects that). */
  sort: SortValue;
  /**
   * Practice-lens filter (2W); `null` is "All practices" — no lens argument is
   * sent. Part of the feed query key, so selecting a practice restarts the
   * cursor walk exactly as a sort change does. Composes with tab and type.
   */
  practiceKey: PracticeKey | null;
}

export interface FeedPage {
  signals: FeedSignal[];
  /** null when the feed is exhausted. */
  nextCursor: string | null;
  /** Total rows matching this tab + type filter (drives "Show 5 more of {n}"). */
  total: number;
  /**
   * Present only when the request carried a practiceKey: the practice-scoped
   * total for this tab + type + practice view. `total` above stays firm-wide
   * across practices (mirroring get_signal_feed's counts), so this is the
   * ONLY honest source for "how many signals are routed to this practice" —
   * never substitute `total` or the firm-wide tab counts for it.
   */
  practiceLens: PracticeLensSummary | null;
}

export interface TriageCounts {
  total: number;
  unclaimedTier1: number;
  followUpsDue: number;
  claimedByMe: number;
  saved: number;
  archived: number;
  /** ISO timestamp of the most recently updated signal. */
  updatedAt: string | null;
  /** ISO timestamp the last completed sweep finished (get_signal_feed
   *  latest_sweep_finished_at); null when no sweep is on record. */
  latestSweepFinishedAt: string | null;
}

export type TabCounts = Record<FeedTab, number>;

/** A teammate a signal can be assigned to (M5 AssignPopover option). */
export interface AssignableUser {
  id: string;
  name: string;
  email: string;
  practice: string | null;
  title: string | null;
}

export type AssignmentNotificationStatus = "sent" | "not_configured" | "failed";

export interface AssignmentResult {
  assigneeName: string;
  assigneeEmail: string;
  notificationStatus: AssignmentNotificationStatus;
}

export interface SignalFeedAdapter {
  getFeedPage(params: FeedPageParams): Promise<FeedPage>;
  getTriageCounts(viewerId: string): Promise<TriageCounts>;
  getTabCounts(viewerId: string): Promise<TabCounts>;
  /** Detail modal fetches its own record by id; viewerId resolves ownership. */
  getSignal(id: string, viewerId: string): Promise<FeedSignal | null>;
  /**
   * Active teammates the viewer can assign a signal to (excludes the viewer).
   * Mock returns a fixed roster; live reads active user_profiles rows.
   */
  listAssignableUsers(viewerId: string): Promise<AssignableUser[]>;
  /**
   * Export the current feed view to CSV (admin/operator only). Stub in Phase D;
   * binds to the server export endpoint at the live swap. Returns a URL to the
   * generated file.
   */
  exportFeedCsv(params: {
    tab: FeedTab;
    signalType: SignalTypeFilter;
    viewerId: string;
    /** Included in the export request only when a practice is selected. */
    practiceKey: PracticeKey | null;
  }): Promise<{ url: string }>;
  /**
   * The active canonical peer practices behind the Signals practice dropdown
   * (2W), in canonical display order. Live calls list_signal_practices once
   * per authenticated session through the existing authenticated invoke path;
   * mock returns an explicit, fixed eight-practice registry.
   */
  listPractices(): Promise<CanonicalPractice[]>;
}

/** Action verbs mirror the Edge Function map in spec §4. */
export type ActionActorType = "human" | "system" | "token_recipient";

export interface ActionHistoryEntry {
  id: string;
  actorType: ActionActorType;
  actorName: string;
  verb: string;
  detail: string | null;
  at: string;
}

export interface FoundSignalContact {
  id: string;
  found_contact_id: string;
  contact_fingerprint: string;
  display_name: string;
  title: string | null;
  email: string | null;
  linkedin: string | null;
  phone: string | null;
  company: string | null;
  source: string;
}

export interface SignalActionsAdapter {
  claim(signalId: string): Promise<void>;
  /**
   * Release a claim — reverts a signal claimed by the viewer back to Unclaimed
   * and writes an `unclaim` ledger event (no new ownership state). Symmetric with
   * claim. Mock reverts in-memory; the live path awaits Codex's `unclaim_signal`
   * Edge Function (see the live adapter).
   */
  unclaim(signalId: string): Promise<void>;
  assign(
    signalId: string,
    assigneeId: string,
    contextNote?: string,
  ): Promise<AssignmentResult>;
  /** Create a magic link for an internal recipient; returns the /s/:token URL. */
  share(signalId: string, recipientId: string): Promise<{ url: string }>;
  archiveWithReason(signalId: string, reason: ArchiveReason): Promise<void>;
  setFollowUp(signalId: string, dueAt: string): Promise<void>;
  /**
   * Run the firm relationship check. Records the ledger event AND returns the
   * grouped-by-owner relationships payload (record_signal_action_event now
   * returns { event, result }); the caller renders the result below the run
   * buttons. Admin-gated server-side (403 for a non-admin, surfaced honestly).
   */
  runRelationshipCheck(signalId: string): Promise<SignalRelationshipResult>;
  runTariffScan(signalId: string): Promise<void>;
  sendAsset(signalId: string, assetId: string): Promise<void>;
  /** Toggle the viewer's personal saved/star state (toggle_signal_save). */
  toggleSave(signalId: string): Promise<{ saved: boolean }>;
  /**
   * Restore an archived signal (R-4). Routes through the restore-signal Edge
   * Function, which calls restore_signal_atomic server-side with the service
   * client — the client never touches the RPC directly (the write gate forbids
   * it). Returns the restored signal's identity, typed off the RPC's own
   * Returns row in shared/types/database.ts.
   *
   * Rejects with SignalActionForbiddenError on 403. The live path is inert
   * until the restore-signal server authorization fix lands on blue; see
   * RESTORE_LIVE_CALL_ENABLED.
   */
  restoreSignal(signalId: string): Promise<RestoredSignal>;
  getActionHistory(signalId: string): Promise<ActionHistoryEntry[]>;
  recordStage(
    signalId: string,
    eventType: "stage_outreach_made" | "stage_meeting_scheduled" | "stage_proposal" | "stage_regressed",
    options?: { toStage?: "assigned" | "stage_outreach_made" | "stage_meeting_scheduled"; reason?: string },
  ): Promise<void>;
  recordOutcome(signalId: string, outcome: "won" | "passed" | "reopen", reason?: string): Promise<void>;
  findContacts(signalId: string, options?: { refresh?: boolean }): Promise<FoundSignalContact[]>;
  saveFoundContact(signalId: string, foundContactId: string): Promise<{ id: string; already_saved: boolean }>;
}

/**
 * Thrown when an Edge Function answers a signal action with HTTP 403 (R-2).
 *
 * The server gates corrections to operators: record_stage_event with
 * event_type=stage_regressed and record_outcome with outcome=reopen both reject
 * a non-admin actor with `{error:{code:"forbidden", …}}`. Before this type the
 * client discarded the status and rendered a generic "try again" retry prompt
 * for an action that can never succeed for that viewer.
 *
 * `message` keeps the pre-existing `"<fn>: <server message>"` shape so nothing
 * that reads it changes; `detail` carries the server's message on its own.
 */
export class SignalActionForbiddenError extends Error {
  readonly detail: string;
  constructor(fn: string, detail: string) {
    super(`${fn}: ${detail}`);
    this.name = "SignalActionForbiddenError";
    this.detail = detail;
  }
}

/**
 * Identity of a signal returned by restore_signal_atomic (R-4).
 *
 * Typed off the generated RPC contract in shared/types/database.ts rather than
 * hand-written, so a schema change surfaces here at compile time. The Edge
 * Function returns the full signals row; the client needs only the identity to
 * confirm the restore and reconcile its caches.
 */
export type RestoredSignal = Pick<
  Database["public"]["Functions"]["restore_signal_atomic"]["Returns"],
  "signal_id" | "status" | "fingerprint"
>;

/**
 * LIVE-CALL GATE (R-4) — OPEN. QB confirmed the restore-signal ownership-or-admin
 * authorization fix is deployed on blue (v7, commit ab01f612), which added
 * requireAssignedSignal + a 403 AuthorizationError mapping to the function. The
 * earlier gap_stands evidence in artifacts/package4-blue-apply-evidence.json
 * predates that deploy and no longer describes the live surface.
 *
 * Kept as a named constant rather than deleted: it is the single kill switch if
 * the server check ever needs to be rolled back.
 */
export const RESTORE_LIVE_CALL_ENABLED = true;

/** Thrown when a restore is attempted while the live-call gate is closed. */
export class RestoreNotActivatedError extends Error {
  constructor(
    message = "Restore is not enabled yet — the server authorization fix is still pending.",
  ) {
    super(message);
    this.name = "RestoreNotActivatedError";
  }
}
